// api.js - API utilities for authentication and data fetching (Auth folder)
import accessManager from './Access';

class APIClient {
  constructor() {
    // Use the centralized access manager
    this.accessManager = accessManager;
    
    // Inactivity management properties
    this.logoutCallback = null;
    this.inactivityWarningCallback = null;
    this.stayLoggedIn = true;
    this.lastActivity = new Date();
    this.inactivityTimer = null;
    this.warningTimer = null;
    
    // Inactivity settings (in milliseconds)
    this.WARNING_TIME = 25 * 60 * 1000; // 25 minutes - show warning
    this.LOGOUT_TIME = 30 * 60 * 1000;  // 30 minutes - auto logout
    
    // Bind methods to preserve context
    this.handleUserActivity = this.handleUserActivity.bind(this);
    this.startInactivityTimer = this.startInactivityTimer.bind(this);
    
    // Set up activity listeners
    this.setupActivityListeners();
  }

  // Helper method to ensure array response
  ensureArray(response) {
    return this.accessManager.ensureArray(response);
  }

  // Activity tracking setup
  setupActivityListeners() {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, this.handleUserActivity, true);
    });
  }

  // Handle user activity
  handleUserActivity() {
    if (this.isAuthenticated()) {
      this.updateLastActivity();
    }
  }

  // Update last activity timestamp
  updateLastActivity() {
    this.lastActivity = new Date();
    this.startInactivityTimer();
  }

  // Start inactivity timer
  startInactivityTimer() {
    // Clear existing timers
    if (this.inactivityTimer) clearTimeout(this.inactivityTimer);
    if (this.warningTimer) clearTimeout(this.warningTimer);

    if (!this.isAuthenticated() || !this.stayLoggedIn) return;

    // Set warning timer (show warning 5 minutes before logout)
    this.warningTimer = setTimeout(() => {
      if (this.inactivityWarningCallback && this.isAuthenticated()) {
        this.inactivityWarningCallback(5); // 5 minutes remaining
      }
    }, this.WARNING_TIME);

    // Set logout timer (logout after 30 minutes of inactivity)
    this.inactivityTimer = setTimeout(() => {
      if (this.logoutCallback && this.isAuthenticated()) {
        console.log('Auto-logout due to inactivity');
        this.logoutCallback();
      }
    }, this.LOGOUT_TIME);
  }

  // Clear inactivity timers
  clearInactivityTimers() {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }

  // Set logout callback for context updates
  setLogoutCallback(callback) {
    this.logoutCallback = callback;
    this.accessManager.setLogoutCallback(callback);
  }

  // Set inactivity warning callback
  setInactivityWarningCallback(callback) {
    this.inactivityWarningCallback = callback;
  }

  // Set stay logged in preference
  setStayLoggedIn(stayLoggedIn) {
    this.stayLoggedIn = stayLoggedIn;
    if (stayLoggedIn) {
      this.startInactivityTimer();
    } else {
      this.clearInactivityTimers();
    }
  }

  // Force token refresh
  async forceRefreshToken() {
    try {
      const result = await this.accessManager.refreshToken();
      this.updateLastActivity();
      return result;
    } catch (error) {
      console.error('Force refresh token error:', error);
      throw error;
    }
  }

  // Get activity information
  getActivityInfo() {
    return {
      lastActivity: this.lastActivity,
      stayLoggedIn: this.stayLoggedIn,
      isOnline: navigator.onLine,
      warningTime: this.WARNING_TIME,
      logoutTime: this.LOGOUT_TIME
    };
  }

  // Get token expiry information
  getTokenExpiryInfo() {
    const token = this.accessManager.getAccessToken ? this.accessManager.getAccessToken() : null;
    if (!token) return null;

    try {
      // Decode JWT token to get expiry
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        expiresAt: new Date(payload.exp * 1000),
        issuedAt: new Date(payload.iat * 1000),
        timeUntilExpiry: (payload.exp * 1000) - Date.now()
      };
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  // Authentication methods
  async login(username, password) {
    try {
      const response = await fetch(`${this.accessManager.getBaseURL()}/auth/login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Login failed');
      }

      const data = await response.json();
      
      // Store tokens using access manager
      this.accessManager.setTokens(data.access, data.refresh, data.user);
      
      // Start activity tracking after successful login
      this.updateLastActivity();
      
      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async refreshToken() {
    try {
      const result = await this.accessManager.refreshToken();
      this.updateLastActivity();
      return result;
    } catch (error) {
      console.error('Refresh token error:', error);
      throw error;
    }
  }

  async logout() {
    try {
      // Clear inactivity timers
      this.clearInactivityTimers();
      
      // Call access manager logout
      const result = await this.accessManager.logout();
      
      return result;
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }

  // User info methods (delegated to access manager)
  getCurrentUser() {
    return this.accessManager.getCurrentUser();
  }

  isAuthenticated() {
    return this.accessManager.isAuthenticated();
  }

  hasRole(role) {
    return this.accessManager.hasRole(role);
  }

  hasAnyRole(roles) {
    return this.accessManager.hasAnyRole(roles);
  }

  // Permission helpers (delegated to access manager)
  canManageUsers() {
    return this.accessManager.canManageUsers();
  }

  canEditUser(userRole) {
    return this.accessManager.canEditUser(userRole);
  }

  canDeleteUser(userRole) {
    return this.accessManager.canDeleteUser(userRole);
  }

  // Validation helpers (delegated to access manager)
  validateUserData(userData, isEdit = false) {
    return this.accessManager.validateUserData(userData, isEdit);
  }

  // Enhanced request method with activity tracking
  async makeRequest(url, options = {}) {
    // Update activity on each request
    if (this.isAuthenticated()) {
      this.updateLastActivity();
    }
    
    return this.accessManager.request(url, options);
  }

  // User management methods (Admin only)
  async getUsers() {
    return this.makeRequest('/auth/users/');
  }

  async createUser(userData) {
    return this.makeRequest('/auth/users/', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  async updateUser(userId, userData) {
    return this.makeRequest(`/auth/users/${userId}/`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
  }

  async deleteUser(userId) {
    return this.makeRequest(`/auth/users/${userId}/`, {
      method: 'DELETE'
    });
  }

  // Branch methods (Admin and Secretary)
  async getBranches() {
    const response = await this.makeRequest('/auth/branches/');
    return this.ensureArray(response);
  }

  async createBranch(branchData) {
    return this.makeRequest('/auth/branches/', {
      method: 'POST',
      body: JSON.stringify(branchData)
    });
  }

  async updateBranch(branchId, branchData) {
    return this.makeRequest(`/auth/branches/${branchId}/`, {
      method: 'PUT',
      body: JSON.stringify(branchData)
    });
  }

  async deleteBranch(branchId) {
    return this.makeRequest(`/auth/branches/${branchId}/`, {
      method: 'DELETE'
    });
  }

  async bulkUpdateBranches(branchesData) {
    return this.makeRequest('/auth/branches/bulk-update/', {
      method: 'POST',
      body: JSON.stringify({ branches: branchesData })
    });
  }

  // Company variables methods (Admin only)
  async getCompanyVariables() {
    return this.makeRequest('/auth/company-variables/');
  }

  async createCompanyVariable(variableData) {
    return this.makeRequest('/auth/company-variables/', {
      method: 'POST',
      body: JSON.stringify(variableData)
    });
  }

  async updateCompanyVariable(variableId, variableData) {
    return this.makeRequest(`/auth/company-variables/${variableId}/`, {
      method: 'PUT',
      body: JSON.stringify(variableData)
    });
  }

  async deleteCompanyVariable(variableId) {
    return this.makeRequest(`/auth/company-variables/${variableId}/`, {
      method: 'DELETE'
    });
  }

  async bulkUpdateVariables(variablesData) {
    return this.makeRequest('/auth/company-variables/bulk-update/', {
      method: 'POST',
      body: JSON.stringify({ variables: variablesData })
    });
  }

  // API configuration methods (Admin only)
  async getAPIConfigurations() {
    const response = await this.makeRequest('/auth/api-configurations/');
    return this.ensureArray(response);
  }

  async createAPIConfiguration(configData) {
    return this.makeRequest('/auth/api-configurations/', {
      method: 'POST',
      body: JSON.stringify(configData)
    });
  }

  async updateAPIConfiguration(configId, configData) {
    return this.makeRequest(`/auth/api-configurations/${configId}/`, {
      method: 'PUT',
      body: JSON.stringify(configData)
    });
  }

  async deleteAPIConfiguration(configId) {
    return this.makeRequest(`/auth/api-configurations/${configId}/`, {
      method: 'DELETE'
    });
  }

  // Admin functions
  async changeAdminCredentials(credentialsData) {
    return this.makeRequest('/auth/change-admin-credentials/', {
      method: 'POST',
      body: JSON.stringify(credentialsData)
    });
  }

  async signOutAllDevices() {
    return this.makeRequest('/auth/sign-out-all-devices/', {
      method: 'POST'
    });
  }

  // Two-Factor Authentication methods
  async getTwoFactorStatus() {
    return this.makeRequest('/auth/two-factor-status/');
  }

  async enableTwoFactor() {
    return this.makeRequest('/auth/two-factor/enable/', {
      method: 'POST'
    });
  }

  async disableTwoFactor() {
    return this.makeRequest('/auth/two-factor/disable/', {
      method: 'POST'
    });
  }

  // Enhanced user management methods
  async getUsersWithDetails() {
    const response = await this.makeRequest('/auth/users/');
    return this.ensureArray(response);
  }

  async createUserWithBranch(userData) {
    return this.makeRequest('/auth/users/', {
      method: 'POST',
      body: JSON.stringify({
        ...userData,
        // Handle branch assignment by ID (consistent with API configs)
        branch_id: userData.branch_id === 'all' || !userData.branch_id ? null : userData.branch_id
      })
    });
  }

  async updateUserWithBranch(userId, userData) {
    return this.makeRequest(`/auth/users/${userId}/`, {
      method: 'PUT',
      body: JSON.stringify({
        ...userData,
        branch_id: userData.branch_id === 'all' || !userData.branch_id ? null : userData.branch_id
      })
    });
  }

  // Admin credential management
  async updateAdminCredentials(credentialsData) {
    return this.makeRequest('/auth/change-admin-credentials/', {
      method: 'POST',
      body: JSON.stringify(credentialsData)
    });
  }

  // Session management
  async signOutAllUserDevices() {
    return this.makeRequest('/auth/sign-out-all-devices/', {
      method: 'POST'
    });
  }

  // Cleanup method
  destroy() {
    // Remove activity listeners
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.removeEventListener(event, this.handleUserActivity, true);
    });
    
    // Clear timers
    this.clearInactivityTimers();
  }
}

// Create and export a singleton instance
const apiClient = new APIClient();

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  apiClient.destroy();
});

export default apiClient;